import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import moment from 'moment';
import { errorService } from '../../services/ErrorService';
import { showErrorToast, showSuccessToast } from '../../utils/ToastUtils';
import { LegalDocumentService } from '../../service/LegalDocumentService';
import {
    selectLoading,
    selectLegalDocumentList,
    getLegalDocumentsAsync,
    createLegalDocumentDraftAsync,
    deleteLegalDocumentDraftAsync,
    publishLegalDocumentAsync,
} from './legalDocumentsSlice';

const legalDocumentService = new LegalDocumentService();

const TYPE_OPTIONS = [
    { label: 'Política de Privacidade', value: 'privacy_policy' },
    { label: 'Termos de Uso', value: 'terms_of_use' },
];

const formatDateTime = (value) => (value ? moment(value).locale('pt-br').format('DD/MM/YYYY HH:mm') : '—');

const LegalDocumentsView = () => {
    const [type, setType] = useState(TYPE_OPTIONS[0].value);

    const [draftDialogVisible, setDraftDialogVisible] = useState(false);
    const [draftContent, setDraftContent] = useState('');

    const [publishTarget, setPublishTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [viewContentDoc, setViewContentDoc] = useState(null);

    const [notifyTarget, setNotifyTarget] = useState(null);
    const [notifyPreviewCount, setNotifyPreviewCount] = useState(null);
    const [notifyPreviewLoading, setNotifyPreviewLoading] = useState(false);
    const [notifySending, setNotifySending] = useState(false);

    const toast = useRef(null);
    const dispatch = useDispatch();
    const loading = useSelector(selectLoading);
    const documentList = useSelector(selectLegalDocumentList);

    const loadData = async () => {
        try {
            await errorService.retryOperation(async () => {
                await dispatch(getLegalDocumentsAsync({ type })).unwrap();
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao carregar documentos', error);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type]);

    // Pré-preenche o rascunho com o texto da versão mais recente (publicada
    // ou não), pra o admin editar em cima em vez de reescrever tudo.
    const openNewDraft = () => {
        setDraftContent(documentList[0]?.content ?? '');
        setDraftDialogVisible(true);
    };

    const hideDraftDialog = () => setDraftDialogVisible(false);

    const onSaveDraft = async () => {
        if (!draftContent.trim()) {
            showErrorToast(toast, 'Erro de validação', 'O conteúdo não pode ficar em branco.');
            return;
        }
        try {
            await errorService.retryOperation(async () => {
                await dispatch(createLegalDocumentDraftAsync({ type, content: draftContent })).unwrap();
                showSuccessToast(toast, 'Sucesso', 'Rascunho criado com sucesso.');
                hideDraftDialog();
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao criar rascunho', error);
        }
    };

    const confirmPublish = (doc) => setPublishTarget(doc);
    const hidePublishDialog = () => setPublishTarget(null);

    const onPublish = async () => {
        try {
            await errorService.retryOperation(async () => {
                await dispatch(publishLegalDocumentAsync({ id: publishTarget.id, type })).unwrap();
                showSuccessToast(toast, 'Sucesso', `Versão ${publishTarget.version} publicada — agora é a vigente no app.`);
                hidePublishDialog();
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao publicar', error);
        }
    };

    const confirmDelete = (doc) => setDeleteTarget(doc);
    const hideDeleteDialog = () => setDeleteTarget(null);

    const onDelete = async () => {
        try {
            await errorService.retryOperation(async () => {
                await dispatch(deleteLegalDocumentDraftAsync({ id: deleteTarget.id, type })).unwrap();
                showSuccessToast(toast, 'Sucesso', `Rascunho da versão ${deleteTarget.version} excluído.`);
                hideDeleteDialog();
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao excluir rascunho', error);
        }
    };

    // Antes de abrir a confirmação, busca quantos usuários com assinatura
    // ativa receberiam o e-mail — o envio tem custo real de AWS, então o
    // admin precisa ver o número antes de confirmar.
    const openNotifyDialog = async (doc) => {
        setNotifyTarget(doc);
        setNotifyPreviewCount(null);
        setNotifyPreviewLoading(true);
        try {
            const { recipientCount } = await legalDocumentService.getNotifyPreview(doc.id);
            setNotifyPreviewCount(recipientCount);
        } catch (error) {
            showErrorToast(toast, 'Erro ao calcular destinatários', error);
            setNotifyTarget(null);
        } finally {
            setNotifyPreviewLoading(false);
        }
    };

    const hideNotifyDialog = () => {
        setNotifyTarget(null);
        setNotifyPreviewCount(null);
    };

    const onConfirmNotify = async () => {
        setNotifySending(true);
        try {
            const result = await legalDocumentService.notify(notifyTarget.id);
            showSuccessToast(
                toast,
                'Notificação enviada',
                `${result.emailsSent} e-mail(s) e ${result.pushSent} push(es) enviados para ${result.recipientCount} usuário(s) com assinatura ativa.`
            );
            hideNotifyDialog();
            await loadData();
        } catch (error) {
            showErrorToast(toast, 'Erro ao notificar usuários', error);
        } finally {
            setNotifySending(false);
        }
    };

    const statusBody = (rowData) => (
        <Tag
            value={rowData.status === 'published' ? 'Publicado' : 'Rascunho'}
            severity={rowData.status === 'published' ? 'success' : 'warning'}
        />
    );

    const notifiedBody = (rowData) => {
        if (rowData.status !== 'published') return '—';
        return rowData.notifiedAt ? (
            <span>Notificado em {formatDateTime(rowData.notifiedAt)}</span>
        ) : (
            <Tag value="Não notificado" severity="info" />
        );
    };

    const actionsBody = (rowData) => (
        <div className="flex gap-2">
            <Button
                label="Ver texto"
                icon="pi pi-eye"
                className="p-button-text p-button-sm"
                onClick={() => setViewContentDoc(rowData)}
            />
            {rowData.status === 'draft' && (
                <Button
                    label="Publicar"
                    icon="pi pi-check"
                    className="p-button-success p-button-sm"
                    onClick={() => confirmPublish(rowData)}
                />
            )}
            {rowData.status === 'draft' && (
                <Button
                    label="Excluir"
                    icon="pi pi-trash"
                    className="p-button-danger p-button-text p-button-sm"
                    onClick={() => confirmDelete(rowData)}
                />
            )}
            {rowData.status === 'published' && !rowData.notifiedAt && (
                <Button
                    label="Notificar usuários"
                    icon="pi pi-send"
                    className="p-button-warning p-button-sm"
                    onClick={() => openNotifyDialog(rowData)}
                />
            )}
        </div>
    );

    const draftDialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={hideDraftDialog} />
            <Button label="Salvar rascunho" icon="pi pi-check" className="p-button-text" onClick={onSaveDraft} />
        </>
    );

    const publishDialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={hidePublishDialog} />
            <Button label="Publicar" icon="pi pi-check" className="p-button-text" onClick={onPublish} />
        </>
    );

    const deleteDialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={hideDeleteDialog} />
            <Button label="Excluir" icon="pi pi-trash" className="p-button-text p-button-danger" onClick={onDelete} />
        </>
    );

    const notifyDialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={hideNotifyDialog} disabled={notifySending} />
            <Button
                label="Confirmar envio"
                icon="pi pi-send"
                className="p-button-text"
                onClick={onConfirmNotify}
                disabled={notifyPreviewLoading || notifySending || !notifyPreviewCount}
                loading={notifySending}
            />
        </>
    );

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <div className="flex justify-content-between align-items-center mb-3">
                        <h5 className="m-0">Política de Privacidade e Termos de Uso</h5>
                        <Button icon="pi pi-refresh" rounded text onClick={loadData} disabled={loading} />
                    </div>

                    <div className="flex flex-column md:flex-row md:align-items-center gap-3 mb-3">
                        <Dropdown value={type} options={TYPE_OPTIONS} onChange={(e) => setType(e.value)} className="w-full md:w-20rem" />
                        <Button label="Novo rascunho" icon="pi pi-plus" className="p-button-success" onClick={openNewDraft} />
                    </div>

                    <DataTable
                        value={loading ? Array.from({ length: 5 }, (_, i) => ({ id: `skeleton-${i}` })) : documentList}
                        dataKey="id"
                        responsiveLayout="scroll"
                        emptyMessage="Nenhuma versão criada ainda para este documento."
                    >
                        <Column field="version" header="Versão" body={loading ? () => <Skeleton /> : undefined} style={{ width: '6rem' }} />
                        <Column field="status" header="Status" body={loading ? () => <Skeleton /> : statusBody} style={{ width: '10rem' }} />
                        <Column field="createdAt" header="Criado em" body={loading ? () => <Skeleton /> : (r) => formatDateTime(r.createdAt)} style={{ width: '12rem' }} />
                        <Column field="publishedAt" header="Publicado em" body={loading ? () => <Skeleton /> : (r) => formatDateTime(r.publishedAt)} style={{ width: '12rem' }} />
                        <Column field="notifiedAt" header="Notificação" body={loading ? () => <Skeleton /> : notifiedBody} style={{ width: '14rem' }} />
                        <Column body={loading ? () => <Skeleton /> : actionsBody} style={{ width: '20rem' }} />
                    </DataTable>

                    {/* Novo rascunho */}
                    <Dialog visible={draftDialogVisible} style={{ width: '700px' }} header="Novo rascunho" modal className="p-fluid" footer={draftDialogFooter} onHide={hideDraftDialog}>
                        <div className="field">
                            <label htmlFor="draftContent">Conteúdo</label>
                            <InputTextarea id="draftContent" value={draftContent} onChange={(e) => setDraftContent(e.target.value)} rows={20} autoResize />
                        </div>
                    </Dialog>

                    {/* Ver texto (qualquer versão, rascunho ou publicada) */}
                    <Dialog visible={!!viewContentDoc} style={{ width: '700px' }} header={viewContentDoc ? `Versão ${viewContentDoc.version}` : ''} modal onHide={() => setViewContentDoc(null)}>
                        <div style={{ whiteSpace: 'pre-wrap', maxHeight: '60vh', overflowY: 'auto' }}>{viewContentDoc?.content}</div>
                    </Dialog>

                    {/* Confirmar publicação */}
                    <Dialog visible={!!publishTarget} style={{ width: '450px' }} header="Confirmar publicação" modal footer={publishDialogFooter} onHide={hidePublishDialog}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            {publishTarget && (
                                <span>
                                    Publicar a versão <b>{publishTarget.version}</b>? Ela passa a ser a vigente para quem abrir o app — a versão atual deixa de aparecer.
                                </span>
                            )}
                        </div>
                    </Dialog>

                    {/* Confirmar exclusão de rascunho */}
                    <Dialog visible={!!deleteTarget} style={{ width: '450px' }} header="Confirmar exclusão" modal footer={deleteDialogFooter} onHide={hideDeleteDialog}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            {deleteTarget && (
                                <span>
                                    Excluir o rascunho da versão <b>{deleteTarget.version}</b>? Essa ação não pode ser desfeita.
                                </span>
                            )}
                        </div>
                    </Dialog>

                    {/* Confirmar notificação (custo real de e-mail) */}
                    <Dialog visible={!!notifyTarget} style={{ width: '450px' }} header="Notificar usuários" modal footer={notifyDialogFooter} onHide={hideNotifyDialog}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-send mr-3" style={{ fontSize: '2rem' }} />
                            {notifyPreviewLoading && <span>Calculando destinatários...</span>}
                            {!notifyPreviewLoading && notifyTarget && (
                                <span>
                                    Isso enviará e-mail e push para <b>{notifyPreviewCount}</b> usuário(s) com assinatura ativa. O e-mail tem custo real de envio e essa versão não poderá ser notificada de novo. Confirma o envio?
                                </span>
                            )}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default LegalDocumentsView;
